import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { LegacyButton } from '@/components/LegacyButton';
import { NextImage } from '@/components/NextImage';
import { Typography } from '@/components/Typography';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { CountryCode } from '@/graphql/saleor/api';
import {
  DEFAULT_V12_DEPOSIT_PERCENT,
  DEFAULT_V12_TERM_DURATION,
} from '@/lib/const';
import {
  calculate,
  getFinanceProduct,
  isV12Available,
  PRICE_UPPER_BOUND,
} from '@/lib/payment/v12';
import { useConfigProvider } from '@/providers/ConfigProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { V12LoanCalculatorModal } from '@/views/PaymentView/components/V12Finance/LoanCalculatorModal';
import * as M from '@/views/PaymentView/components/V12Finance/messages';
import { type V12Payments } from '@/views/PaymentView/components/V12Finance/type';

import { InstallmentInfoSection, Wrapper } from './V12Sentence.style';

import v12Available from '@/assets/payment_methods/v12.png';

interface V12SentenceProps {
  amount: number;
  country: CountryCode;
  currency: string;
  isLoading: boolean;
  isProcessing: boolean;
  onAddToCart: () => Promise<void>;
  variantCTA: OrderAppCtaType;
}

export const V12Sentence = ({
  amount,
  country,
  currency,
  isLoading,
  isProcessing,
  onAddToCart,
  variantCTA,
}: V12SentenceProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { v12PaymentMax, v12PaymentMin } = useConfigProvider();
  const { formatPrice } = useRegionProvider();
  const [payments, setPayments] = useState<V12Payments | null>(null);
  const { formatMessage } = useIntl();

  useEffect(() => {
    const cashPrice = amount;
    const financeProduct = getFinanceProduct(DEFAULT_V12_TERM_DURATION);
    const deposit =
      cashPrice > PRICE_UPPER_BOUND && financeProduct
        ? cashPrice - financeProduct.maxLoan
        : parseFloat(
            (Math.ceil(cashPrice * DEFAULT_V12_DEPOSIT_PERCENT) / 100).toFixed(
              2,
            ),
          );

    const calcPayments = calculate(financeProduct, cashPrice, deposit);
    setPayments(calcPayments);
  }, []);

  if (
    !isV12Available(amount, country, variantCTA, v12PaymentMin, v12PaymentMax)
  )
    return null;

  return (
    <>
      <Wrapper data-test-id='V12Sentence'>
        <Typography data-test-id='V12InstallmentAmountText'>
          {payments
            ? formatMessage(M.defined.installmentInfoPrefix, {
                amount: formatPrice(
                  {
                    amount: Math.ceil(Number(payments.initialPayments)),
                    currency,
                  },
                  { maximumFractionDigits: 2 },
                ),
              })
            : ''}
          &nbsp;
          <span
            className='mobile-only'
            data-test-id='V12InstallmentWithText-Desktop'
          >
            {formatMessage(M.defined.installmentInfoWith)}
          </span>
        </Typography>
        <InstallmentInfoSection data-test-id='InstallmentInfoSection'>
          <Typography
            className='desktop-only'
            data-test-id='V12InstallmentWithText-Mobile'
          >
            {formatMessage(M.defined.installmentInfoWith)}
          </Typography>
          <NextImage
            alt='v12 available'
            data-test-id='V12AvailableImage'
            height='60'
            onClick={() => setIsOpen(true)}
            src={v12Available}
            width='auto'
          />
          <LegacyButton
            className='view-options'
            data-test-id='V12ViewOptionsButton'
            isUpperCase={false}
            label={formatMessage(M.defined.installmentInfoViewOptions)}
            ml='s050'
            onClick={() => setIsOpen(true)}
            type='button'
            variant='link'
          />
        </InstallmentInfoSection>
      </Wrapper>
      <V12LoanCalculatorModal
        amount={amount}
        currency={currency}
        isLoading={isLoading}
        isOpen={isOpen}
        isProcessing={isProcessing}
        onAddToCart={onAddToCart}
        onClose={() => setIsOpen(false)}
        variantCTA={variantCTA}
      />
    </>
  );
};
